<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { useVuelidate } from '@vuelidate/core';
import * as validators from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import Swal from 'sweetalert2';

// Composables
import useValidationErrors from '@/composables/useValidationErrors';
import useItems from '@/composables/useItems';
import useErrorMessages from '@/composables/useErrorMessages';
import { MAX_FILE_BYTES, MAX_FILE_MB } from '@/helpers/Limits';
import fileToBase64 from '@/helpers/FileToBase64';

const { t } = useI18n();

const {
  loading, initialState: state,
} = storeToRefs(useAppStore());

const { setDocumentos } = useAppStore();

const especializacioItems = useItems('FORMACIONES', 'educacion', 'ATEXT', 'AUSBI', false, 'titulo');
const entidades = useItems('INST_ACADEMICAS', 'educacion', 'INSTI', 'SCHCD', false, 'entidadEducativa');
const universidadItems = computed(() => entidades.value.filter((ent: any) => ent.slart === '04'));

const { createI18nMessage } = validators;
const messagePath = ({ $validator, $params }: any) => {
  const fieldName = $params?.$field || $validator;
  return `$vuetify.validations.${fieldName}`;
};

const withI18nMessage = createI18nMessage({ t, messagePath });
const required = withI18nMessage(validators.required);
const requiredIf = (condition: () => boolean) => withI18nMessage(validators.requiredIf(condition));
const minLength = (length: number) => withI18nMessage(validators.minLength(length));
// const maxLength = (length: number) => withI18nMessage(validators.maxLength(length));
// const numeric = withI18nMessage(validators.numeric);

const rules = computed(() => ({
  datosPasantia: {
    carrera: { required },
    otraCarrera: {
      requiredIf: requiredIf(() => {
        const { carrera } = state.value.datosPasantia;
        if (carrera && 'value' in carrera) {
          return carrera.value === 2320;
        }
        return false;
      }),
      minLength: minLength(5),
    },
    universidad: { required },
    otraUniversidad: {
      requiredIf: requiredIf(() => {
        const { universidad } = state.value.datosPasantia;
        if (universidad && 'value' in universidad) {
          return universidad.value === 999;
        }
        return false;
      }),
      minLength: minLength(5),
    },
    calificacion: { required },
  },
}));

const rulesFile = {
  required: (nombre: string) => (value: FileList | null) => (value && value.length > 0) || t('$vuetify.validations.required', { field: t(`$vuetify.datosPasantia.${nombre}`).toLowerCase() }),
  requiredValidFile: (value: FileList | null) => {
    if (value && value.length > 0) {
      const extension = value[0].name.split('.').pop()?.toLowerCase();
      if (extension === 'pdf' || extension === 'docx') {
        return true;
      }
    }
    return t('$vuetify.validations.filetype');
  },
  // requiredValidFile: (value: FileList | null) => !value
  // || !value.length || value[0].type === 'application/pdf' || t('$vuetify.validations.filetype'),
  rulesFiles: (value: any) => !value || !value.length || value[0].size < MAX_FILE_BYTES || t('$vuetify.validations.fileweight', { mb: MAX_FILE_MB }),
};

const v$ = useVuelidate(rules, state);

const errors = computed(() => useValidationErrors<any>(v$.value.$errors));
const { getErrorMessages } = useErrorMessages(v$, errors);

const formFile = ref();

// const archivoCV = ref() as Ref<File>;
// const { base64 } = useBase64(archivoCV);

async function onFileInput(e: Event, name: string) {
  const [file] = (e.target as HTMLInputElement).files!;
  const extension = file.name.split('.').pop()?.toLowerCase() || '';

  // Se descarta antes de convertir: un archivo invalido no debe ocupar el
  // store ni pagar el costo del base64, y si quedara guardado se enviaria
  // aunque las reglas del formulario lo hayan rechazado en pantalla.
  if (file.size >= MAX_FILE_BYTES || extension !== 'pdf') {
    setDocumentos(name, '', '');
    return;
  }

  try {
    setDocumentos(name, await fileToBase64(file), extension);
  } catch {
    setDocumentos(name, '', '');
    Swal.fire({ text: t('$vuetify.errores.lecturaArchivo'), icon: 'error' });
  }
}

async function submitForm() {
  const { valid }: { valid: boolean } = await formFile.value.validate();

  const check = await v$.value.$validate();
  if (valid && check) {
    // setArchivoCV(base64.value);
    return true;
  }
  return false;
}

defineExpose({
  submitForm,
});

const filterKeys = (e: KeyboardEvent) => {
  const { key } = e;

  // Permitir teclas de control como Backspace, Delete, flechas, etc.
  if (
    key === 'Backspace' || key === 'Tab' || key === 'ArrowLeft'
    || key === 'ArrowRight' || key === 'Delete'
  ) return;

  // Solo permitir números y un punto
  if (!/^[0-9.]$/.test(key)) {
    e.preventDefault();
    return;
  }

  const input = e.target as HTMLInputElement;
  const { value, selectionStart, selectionEnd } = input;

  // Simular el nuevo valor con la tecla presionada
  const newValue = value.substring(0, selectionStart ?? 0)
    + key
    + value.substring(selectionEnd ?? 0);

  // No permitir más de un punto decimal
  if ((key === '.' && value.includes('.')) || newValue.split('.').length > 2) {
    e.preventDefault();
    return;
  }

  // Limitar a dos decimales
  const parts = newValue.split('.');
  if (parts[1] && parts[1].length > 2) {
    e.preventDefault();
    return;
  }

  // No permitir más de 6 caracteres en total
  if (newValue.length > 6) {
    e.preventDefault();
    return;
  }

  // Validar que el número no exceda 100
  const num = parseFloat(newValue);
  if (!Number.isNaN(num) && num > 100) {
    e.preventDefault();
    return;
  }

  // No permitir que empiece con punto
  if (newValue === '.' || newValue.startsWith('.')) {
    e.preventDefault();
  }
};

</script>
<template>
  <v-form @submit.prevent="" ref="formFile" :disabled="loading">
    <v-sheet
      class="mb-5"
      border>
      <v-container class="pa-0">
        <v-row
          no-gutters
        >
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <AutocompleteComponent
                v-model="state.datosPasantia.carrera"
                :label="$t('$vuetify.datosPasantia.carrera')"
                :items="especializacioItems"
                required="required"
                :error="v$.datosPasantia.carrera.$error"
                :errorMessages="getErrorMessages('datosPasantia.carrera')"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
            v-if=" state.datosPasantia.carrera && state.datosPasantia.carrera.value === 2320"
          >
            <v-sheet class="ma-2">
              <InputTextComponent
                v-model.trim="state.datosPasantia.otraCarrera"
                :label="$t('$vuetify.datosPasantia.otraCarrera')"
                required="required"
                :error="v$.datosPasantia.otraCarrera.$error"
                :errorMessages="getErrorMessages('datosPasantia.otraCarrera')"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <AutocompleteComponent
                v-model="state.datosPasantia.universidad"
                :label="$t('$vuetify.datosPasantia.universidad')"
                :items="universidadItems"
                required="required"
                :error="v$.datosPasantia.universidad.$error"
                :errorMessages="getErrorMessages('datosPasantia.universidad')"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
            v-if="state.datosPasantia.universidad
              && state.datosPasantia.universidad.value === 999"
          >
            <v-sheet class="ma-2">
              <InputTextComponent
                v-model.trim="state.datosPasantia.otraUniversidad"
                :label="$t('$vuetify.datosPasantia.otraUniversidad')"
                required="required"
                :error="v$.datosPasantia.otraUniversidad.$error"
                :errorMessages="getErrorMessages('datosPasantia.otraUniversidad')"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <!-- <v-number-input
                :precision="2"
                :max="100"
                :min="1"
                hide-details="auto" /> -->
              <InputTextComponent
                v-model.trim="state.datosPasantia.calificacion"
                :label="$t('$vuetify.datosPasantia.calificacion')"
                required="required"
                @keypress="filterKeys"
                :error="v$.datosPasantia.calificacion.$error"
                :errorMessages="getErrorMessages('datosPasantia.calificacion')"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <FileComponent
                :label="$t('$vuetify.datosPasantia.cartaUniversidad')"
                :hint="$t('$vuetify.archivohint')"
                required="required"
                @input="onFileInput($event, 'cartaUniversidad')"
                :rules="[rulesFile.required('cartaUniversidad'), rulesFile.requiredValidFile, rulesFile.rulesFiles]"
                accept=".pdf"
              />
            </v-sheet>
          </v-col>
        </v-row>
        <v-row
          no-gutters
        >
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <FileComponent
                :label="$t('$vuetify.datosPasantia.curriculum')"
                :hint="$t('$vuetify.archivohint')"
                required="required"
                @input="onFileInput($event, 'curriculum')"
                :rules="[rulesFile.required('curriculum'), rulesFile.requiredValidFile, rulesFile.rulesFiles]"
                accept=".pdf"
              />
            </v-sheet>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-sheet class="ma-2">
              <FileComponent
                :label="$t('$vuetify.datosPasantia.recordNotas')"
                :hint="$t('$vuetify.datosPasantia.recordNotasHint')"
                required="required"
                @input="onFileInput($event, 'recordNotas')"
                :rules="[rulesFile.required('recordNotas'), rulesFile.requiredValidFile, rulesFile.rulesFiles]"
                accept=".pdf"
              />
            </v-sheet>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
  </v-form>
</template>

<style scoped>

</style>
